/* eslint-disable */
import { useCallback, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import type { Data } from '../models/dealer';
import { BY_GEOLOCATION } from '../utils/graphql/dealers';
import { toISO3CountryCode } from '../utils/i18n';

type Vars = {
  lat: number;
  lng: number;
  userLat?: number;
  userLng?: number;
  radius: number;
  country?: string;
  services?: string;
};

export default function useLazySearchByGeoLocation(
  radiusInKm: number,
  lat: number,
  lng: number,
  countryCode?: string,
  filters?: string[],
  userLat?: number,
  userLng?: number,
) {
  const [load, { error, data, loading }] = useLazyQuery<Data, Vars>(BY_GEOLOCATION, {
    fetchPolicy: 'no-cache',
    context: { headers: { alwaysReturnPartner: true } },
    notifyOnNetworkStatusChange: false,
  });

  const lastKeyRef = useRef<string | null>(null);

  const loadDealers = useCallback(() => {
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0);

    const validRadius = Number.isFinite(radiusInKm) && radiusInKm > 0;

    if (!hasCoords || !validRadius || loading) return;

    const vars: Vars = {
      lat,
      lng,
      radius: radiusInKm,
      ...(countryCode && { country: toISO3CountryCode(countryCode) }),
      ...(filters && filters.length > 0 ? { services: filters.join() } : {}),
      userLat,
      userLng,
    };

    const key = JSON.stringify(vars);
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    load({ variables: vars });
  }, [lat, lng, radiusInKm, countryCode, filters, userLat, userLng, load, loading]);

  return {
    error,
    loading,
    dealersResult: data?.dealersByGeoLocation.dealers,
    searchRadius: data?.dealersByGeoLocation.meta?.searchRadius,
    loadDealers,
  };
}
